import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Search, MessageSquare, Clock, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VoteButtons from "@/components/VoteButtons";
import ShareButtons from "@/components/ShareButtons";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const STATUS_STYLE = {
  pending: "text-amber-400 border-amber-400/30",
  blocked: "text-[#FF5E7E] border-[#FF5E7E]/30",
  rejected: "text-slate-500 border-slate-500/30",
  approved: "text-emerald-400 border-emerald-400/30",
};

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("hot");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "General" });
  const [posting, setPosting] = useState(false);

  const loadThreads = () => {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (category) params.category = category;
    api.get("/community/threads", { params })
      .then(({ data }) => setThreads(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/community/meta").then(({ data }) => setCategories(data.categories)); }, []);
  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, [sort, category]);
  useEffect(() => {
    if (user) api.get("/community/threads/me").then(({ data }) => setMine(data.filter((t) => t.status !== "approved")));
    else setMine([]);
  }, [user]);

  const openForm = () => {
    if (!user) { toast.error("Please log in to start a thread"); navigate("/login"); return; }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/community/threads", form);
      if (data.status === "blocked") toast.warning(data.message);
      else toast.success(data.message);
      setShowForm(false);
      setForm({ title: "", body: "", category: "General" });
      api.get("/community/threads/me").then(({ data }) => setMine(data.filter((t) => t.status !== "approved")));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-mono-caption uppercase tracking-[0.3em] text-fuchsia-400 mb-3">Traveler Community</p>
            <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-tight">
              The <span className="text-gradient-warm">Forum</span>
            </h1>
            <p className="text-slate-400 text-base mt-3">Trade tips, trip reports and questions with fellow Caribbean travelers.</p>
          </div>
          <button data-testid="new-thread-btn" onClick={openForm} className="flex items-center gap-2 px-5 py-3 rounded-full btn-neon font-semibold shrink-0">
            <Plus className="w-4 h-4" /> Start a thread
          </button>
        </div>

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <form onSubmit={(e) => { e.preventDefault(); loadThreads(); }} className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="community-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search threads..."
              className="w-full bg-[#0B1020] border border-[#232A4D] rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60"
            />
          </form>
          <select
            data-testid="community-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-[#0B1020] border border-[#232A4D] rounded-full px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-400/60"
          >
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="community-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#0B1020] border border-[#232A4D] rounded-full px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-400/60"
          >
            <option value="hot">Top</option>
            <option value="new">Newest</option>
          </select>
        </div>

        {/* Pending notice */}
        {mine.length > 0 && (
          <div className="glass rounded-xl p-4 mb-6" data-testid="my-pending">
            <p className="text-sm text-slate-300 mb-2 font-semibold">Your submissions under review</p>
            <div className="space-y-2">
              {mine.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 truncate">{t.title}</span>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[t.status]}`}>{t.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : threads.length === 0 ? (
          <p className="text-slate-500 text-center py-24">No threads yet. Be the first to start the conversation.</p>
        ) : (
          <div className="space-y-3" data-testid="thread-list">
            {threads.map((t) => (
              <div key={t.id} data-testid={`thread-row-${t.id}`} className="glass glass-hover rounded-xl p-4 flex gap-4">
                <VoteButtons type="thread" item={t} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono-caption uppercase tracking-widest px-2 py-0.5 rounded-full glass border border-fuchsia-400/30 text-fuchsia-400">{t.category}</span>
                    <span className="text-xs text-slate-500">{t.author_name} · {timeAgo(t.created_at)}</span>
                  </div>
                  <Link to={`/community/${t.id}`} className="block">
                    <h3 className="font-heading font-bold text-white text-lg leading-tight hover:text-gradient-warm line-clamp-2">{t.title}</h3>
                    {t.body && <p className="text-sm text-slate-400 line-clamp-2 mt-1">{t.body}</p>}
                  </Link>
                  <div className="flex items-center gap-4 mt-3">
                    <Link to={`/community/${t.id}`} className="flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-400">
                      <MessageSquare className="w-3.5 h-3.5" /> {t.comments_count || 0} comments
                    </Link>
                    <ShareButtons title={t.title} path={`/community/${t.id}`} compact />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New thread modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="glass rounded-2xl w-full max-w-lg p-6 border border-[#232A4D]" onClick={(e) => e.stopPropagation()} data-testid="thread-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-extrabold text-white text-2xl">Start a thread</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-amber-400 mb-4">New threads are reviewed by a moderator before appearing publicly.</p>
            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid="thread-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Title"
                required
                className="w-full bg-[#0B1020] border border-[#232A4D] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60"
              />
              <select
                data-testid="thread-category-input"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-[#0B1020] border border-[#232A4D] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-cyan-400/60"
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea
                data-testid="thread-body-input"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="What's on your mind?"
                rows={5}
                className="w-full bg-[#0B1020] border border-[#232A4D] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60 resize-none"
              />
              <button data-testid="thread-submit" disabled={posting} className="w-full py-3 rounded-xl btn-neon font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Submit for review</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
